import { DialogTitle } from "@mui/material";
import { dualLangInspectionNoteBusiness } from "api/bussiness/inspection-note/dual-lang-inspection-note/dual-lang-inspection-note-business";
import { productBussiness } from "api/bussiness/product/product-bussiness";
import { workOrderPoBusiness } from "api/bussiness/work-order-po/work-order-po-bussiness";
import { DynamicForm } from "commons/ultilities/dynamic-form/components/dynamic-form";
import { DynamicFormArray } from "commons/ultilities/dynamic-form/components/dynamic-form-array";
import { useForm } from "commons/ultilities/dynamic-form/hooks/use-form";
import {
  IControl,
  IControlProperties,
} from "commons/ultilities/dynamic-form/models/control";
import { createProductOptionLabel } from "commons/ultilities/option-item/label-ultil";
import { cloneDeep } from "lodash";
import { observer } from "mobx-react-lite";

import { PrintColorInspect } from "commons/enum/inspection";
import { IAddDualLangInspectionNote } from "mobx-stores/_models/inspection-note/dual-lang-inspection-note/dual-lang-inspection-note";
import { IAddDualLangInspectionNoteItem } from "mobx-stores/_models/inspection-note/dual-lang-inspection-note/dual-lang-inspection-note-item";
import { IProductProperties } from "mobx-stores/_models/products/product";
import { IWorkOrderPoProperties } from "mobx-stores/_models/work-order-po/work-order-po";
import { useStores } from "mobx-stores/_root-store";
import { useCallback, useEffect, useMemo } from "react";
import { DialogComplexContent } from "share-components/templates/form/dialog-complex-form/dialog-complex-content";
import { DialogComplexFormContainer } from "share-components/templates/form/dialog-complex-form/dialog-complex-form";
import { DialogComplexFormArrayContainer } from "share-components/templates/form/dialog-complex-form/dialog-complex-form-array";
import { DialogForm } from "share-components/templates/form/dialog-form";
import { getAddDualLangInspectionNoteFormSchema } from "../schema";
import {
  apperanceInspectDefaultText,
  approvalInspectDefaultText,
  createApprovalStandard,
  createHeightStandard,
  createLengthStandard,
  createPaperTypeStandard,
  createPrintColorStandard,
  createThicknessStandard,
  createWeightStandard,
  createWidthStandard,
  getOthersStandard,
  inspectionDefaultValues,
  inspectionItemDefaultValues,
  otherInspectDefaultText,
} from "../share";

export const AddDualLangInspectionNote = observer(() => {
  const {
    dualLangInspectionNoteManagement: { openNew, setProp },
    inspectionNoteManagement: { getInspectionNotes },
    setProp: setRootStoreProp,
  } = useStores();
  const {
    form: { controls, isValid },
    getControl,
    getControlArray,
    getArrayItemControl,
    submitHandler,
  } = useForm<IAddDualLangInspectionNote>(
    (form) => getAddDualLangInspectionNoteFormSchema(form),
    {
      ...inspectionDefaultValues,
      items: [
        {
          ...inspectionItemDefaultValues,
        },
      ],
    }
  );

  const disableSomeFieldIfStandardIsEmpty = useCallback(
    (data: IAddDualLangInspectionNoteItem) => {
      if (!data) return;
      // disable some fields if standard is empty
      console.log("data", data);
      getArrayItemControl("items", 0, "heightInspect").setProp(
        "disabled",
        !data.heightStandard
      );
      getArrayItemControl("items", 0, "heightResult").setProp(
        "disabled",
        !data.heightStandard
      );

      getArrayItemControl("items", 0, "lengthInspect").setProp(
        "disabled",
        !data.lengthStandard
      );
      getArrayItemControl("items", 0, "lengthResult").setProp(
        "disabled",
        !data.lengthStandard
      );

      getArrayItemControl("items", 0, "thicknessInspect").setProp(
        "disabled",
        !data.thicknessStandard
      );
      getArrayItemControl("items", 0, "thicknessResult").setProp(
        "disabled",
        !data.thicknessStandard
      );

      getArrayItemControl("items", 0, "weightInspect").setProp(
        "disabled",
        !data.weightStandard
      );
      getArrayItemControl("items", 0, "weightResult").setProp(
        "disabled",
        !data.weightStandard
      );

      getArrayItemControl("items", 0, "widthInspect").setProp(
        "disabled",
        !data.widthStandard
      );
      getArrayItemControl("items", 0, "widthResult").setProp(
        "disabled",
        !data.widthStandard
      );

      getArrayItemControl("items", 0, "approvalInspect").setProp(
        "disabled",
        !data.approvalStandard
      );
      getArrayItemControl("items", 0, "approvalResult").setProp(
        "disabled",
        !data.approvalStandard
      );

      getArrayItemControl("items", 0, "printColorInspect").setProp(
        "disabled",
        !data.printColorStandard
      );
      getArrayItemControl("items", 0, "printColorResult").setProp(
        "disabled",
        !data.printColorStandard
      );
    },
    [getArrayItemControl]
  );

  const saveHandler = useCallback(
    async (data: IAddDualLangInspectionNote) => {
      try {
        await dualLangInspectionNoteBusiness.add(data);
        // await getDualLangInspectionNotes();
        getInspectionNotes();
        setProp("openNew", false);
      } catch (ex) {
        console.error(ex);
      }
    },
    [getInspectionNotes, setProp]
  );

  const itemControl = useMemo(() => {
    return getControl("items");
  }, [getControl]);

  const dualLangInspectionNoteItems = useMemo(() => {
    return getControlArray("items");
  }, [getControlArray]);

  const cleanItems = useCallback(() => {
    if (itemControl) {
      itemControl.setProp("children", []);
    }
  }, [itemControl]);

  const setOptionItemsForProductIsEmty = useCallback(() => {
    if (itemControl) {
      const newChildSchema = cloneDeep(itemControl.childSchema);
      const productIdSchema = newChildSchema.productId as IControlProperties;
      productIdSchema.optionItems = [];

      itemControl.setChildSchema(newChildSchema);
    }
  }, [itemControl]);

  const setOptionItemsForProduct = useCallback(
    (wo: IWorkOrderPoProperties) => {
      if (itemControl) {
        const newChildSchema = cloneDeep(itemControl.childSchema);
        const productIdSchema = newChildSchema.productId as IControlProperties;
        const product = wo.purchaseOrderItem?.product;
        productIdSchema.optionItems = product
          ? [
              {
                label: createProductOptionLabel(product as IProductProperties),
                value: product.id,
              },
            ]
          : [];

        itemControl.setChildSchema(newChildSchema);
      }
    },
    [itemControl]
  );

  const valueChangeHandler = useCallback(
    async (control: IControl) => {
      if (control.name === "customerId") {
        getControl("workOrderPOId").setProp("value", "");
        getControl("purchaseOrderId").setProp("value", "");
        cleanItems();
        setOptionItemsForProductIsEmty();
        itemControl.addChildren<IAddDualLangInspectionNoteItem>([
          {
            ...inspectionItemDefaultValues,
          },
        ]);
      }
      if (control.name === "workOrderPOId") {
        try {
          setRootStoreProp("appFreeze", true);
          if (control.value) {
            cleanItems();
            setOptionItemsForProductIsEmty();
            const wo = await workOrderPoBusiness.getById(control.value);
            setOptionItemsForProduct(wo);

            const woProductId = wo.purchaseOrderItem?.productId;
            if (woProductId) {
              const product = await productBussiness.getById(woProductId);
              const otherStandardInspectionStandard =
                (product.dimensionWeight &&
                  getOthersStandard(product.dimensionWeight)) ||
                "";
              const otherStandardInspect = otherStandardInspectionStandard
                ? otherInspectDefaultText
                : "";
              const newData: IAddDualLangInspectionNoteItem = {
                ...inspectionItemDefaultValues,
                approvalStandard:
                  (product && createApprovalStandard(product)) || "",
                heightStandard:
                  (product && createHeightStandard(product)) || "",
                lengthStandard:
                  (product && createLengthStandard(product)) || "",
                paperTypeInspecctionStandard:
                  (product && createPaperTypeStandard(product)) || "",
                printColorStandard:
                  (product && createPrintColorStandard(product)) || "",
                thicknessStandard:
                  (product && createThicknessStandard(product)) || "",
                weightStandard:
                  (product && createWeightStandard(product)) || "",
                widthStandard: (product && createWidthStandard(product)) || "",
                productId: product.id || "",
                otherStandardInspectionStandard:
                  (product.dimensionWeight &&
                    getOthersStandard(product.dimensionWeight)) ||
                  "",
                otherStandardInspect: otherStandardInspect,
                appearanceInspectionStandard:
                  product.dimensionWeight?.appearanceText ?? "",
              };
              itemControl.addChildren<IAddDualLangInspectionNoteItem>([
                {
                  ...newData,
                  approvalInspect: !newData.approvalStandard
                    ? ""
                    : approvalInspectDefaultText,
                  printColorInspect: !newData.printColorStandard
                    ? ""
                    : PrintColorInspect.Standard,
                  appearanceInspect: newData.appearanceInspectionStandard
                    ? apperanceInspectDefaultText
                    : "",
                },
              ]);

              // disable some fields if standard is empty

              disableSomeFieldIfStandardIsEmpty(newData);
            }
          }
        } catch (ex) {
        } finally {
          setRootStoreProp("appFreeze", false);
        }
      }
    },
    [
      cleanItems,
      disableSomeFieldIfStandardIsEmpty,
      getControl,
      itemControl,
      setOptionItemsForProduct,
      setOptionItemsForProductIsEmty,
      setRootStoreProp,
    ]
  );

  const otherStandardInspectionStandardControl = useMemo(() => {
    return getArrayItemControl("items", 0, "otherStandardInspectionStandard");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getArrayItemControl, itemControl]);

  const otherStandardInspectionStandardValue =
    otherStandardInspectionStandardControl?.value || "";

  useEffect(() => {
    if (!otherStandardInspectionStandardControl) return;

    // Only update if the value is different to avoid infinite loop
    const expectedValue = otherStandardInspectionStandardValue
      ? otherInspectDefaultText
      : "";
    if (otherStandardInspectionStandardControl.value !== expectedValue) {
      getArrayItemControl(
        "items",
        0,
        "otherStandardInspect"
      ).setValueAndValidate(expectedValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    otherStandardInspectionStandardControl,
    otherStandardInspectionStandardValue,
    otherInspectDefaultText,
  ]);

  const onItemsValueChanged = useCallback(
    (control: IControl, rowIndex: number) => {},
    []
  );
  return (
    <DialogForm
      open={openNew}
      fullWidth
      maxWidth={"xl"}
      onClose={() => setProp("openNew", false)}
      onCancelBtnClicked={() => setProp("openNew", false)}
      onSaveBtnClick={async () => await submitHandler(saveHandler)}
      saveBtnProps={{
        disabled: !isValid,
      }}
    >
      <DialogTitle>Thêm phiếu kiểm nghiệm</DialogTitle>
      <DialogComplexContent>
        <DialogComplexFormContainer>
          <DynamicForm
            controls={controls}
            onValueChanged={valueChangeHandler}
          ></DynamicForm>
        </DialogComplexFormContainer>
        <DialogComplexFormArrayContainer label={itemControl.label}>
          {dualLangInspectionNoteItems &&
            dualLangInspectionNoteItems.length > 0 && (
              <DynamicFormArray
                containerProps={{
                  sx: {
                    marginTop: "10px",
                    marginBottom: "10px",
                  },
                  spacing: 2,
                }}
                controlSpacing={2}
                children={dualLangInspectionNoteItems}
                onValueChanged={onItemsValueChanged}
              ></DynamicFormArray>
            )}
        </DialogComplexFormArrayContainer>
      </DialogComplexContent>
    </DialogForm>
  );
});
